import {
  paths as tenantPaths,
  components as tenantComponents,
} from "@securityjourney/svc-settings/src/gen/settings-tenant";
import { paths as userPaths } from "@securityjourney/svc-settings/src/gen/settings-user";
import { paths as backOfficePaths } from "@securityjourney/svc-settings/src/gen/settings-backoffice";
import { ServiceClient, TokenFunc, Call, ServiceError } from "@securityjourney/service-client";
import { retrieveAuthToken } from "../utils";

type SvcSettingsPaths = tenantPaths & userPaths & backOfficePaths;

export type SettingsGroup =
  tenantComponents["schemas"]["securityjourney.proto.settings.SettingsGroup"];
export type SettingsGroupList =
  tenantComponents["schemas"]["securityjourney.proto.settings.SettingsGroupList"];
export type FieldAttributes =
  tenantComponents["schemas"]["securityjourney.proto.settings.FieldAttributes"];

// The `values` field of every update-settings request body is a freeform
// `google.protobuf.Struct` (`{ [key: string]: Value }`). Callers supply opaque
// `Record<string, unknown>` setting values that are valid JSON values at runtime,
// so we cast to the precise generated Struct type rather than `any`.
type SettingsValues = tenantComponents["schemas"]["google.protobuf.Struct"];

export type { FieldInfo, ParsedSchema, EnumValue } from "@securityjourney/svc-settings/src/index";
export { parseSchema, describeFields, describeEnum } from "@securityjourney/svc-settings/src/index";

export class SvcSettingsClient extends ServiceClient<SvcSettingsPaths> {
  constructor(getToken: TokenFunc, url?: string) {
    super({ getToken, url });
  }

  getAllTenantSettings(tenant: string): Call<SettingsGroupList> {
    const func = this.fetcher.path("/svc/settings/tenant/{tenant}").method("get").create();
    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func({ tenant }, init))
        .then(res => res.data as SettingsGroupList)
        .catch(ServiceError.raise),
    };
  }

  getTenantSettings(tenant: string, groupName: string): Call<SettingsGroupList> {
    const func = this.fetcher
      .path("/svc/settings/tenant/{tenant}/{group_name}")
      .method("get")
      .create();
    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func({ tenant, group_name: groupName }, init))
        .then(res => res.data as SettingsGroupList)
        .catch(ServiceError.raise),
    };
  }

  updateTenantSettings(
    tenant: string,
    groupName: string,
    values: Record<string, unknown>
  ): Call<void> {
    const func = this.fetcher
      .path("/svc/settings/tenant/{tenant}/{group_name}")
      .method("post")
      .create();
    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init =>
          func({ tenant, group_name: groupName, values: values as SettingsValues }, init)
        )
        .then(() => undefined)
        .catch(ServiceError.raise),
    };
  }

  getAllUserSettings(): Call<SettingsGroupList> {
    const func = this.fetcher.path("/svc/settings/user").method("get").create();
    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func({}, init))
        .then(res => res.data as SettingsGroupList)
        .catch(ServiceError.raise),
    };
  }

  getUserSettings(groupName: string): Call<SettingsGroupList> {
    const func = this.fetcher.path("/svc/settings/user/{group_name}").method("get").create();
    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func({ group_name: groupName }, init))
        .then(res => res.data as SettingsGroupList)
        .catch(ServiceError.raise),
    };
  }

  updateUserSettings(groupName: string, values: Record<string, unknown>): Call<void> {
    const func = this.fetcher.path("/svc/settings/user/{group_name}").method("post").create();
    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func({ group_name: groupName, values: values as SettingsValues }, init))
        .then(() => undefined)
        .catch(ServiceError.raise),
    };
  }

  getAllBackOfficeSettings(tenant: string): Call<SettingsGroupList> {
    const func = this.fetcher.path("/svc/settings/back-office/{tenant}").method("get").create();
    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func({ tenant }, init))
        .then(res => res.data as SettingsGroupList)
        .catch(ServiceError.raise),
    };
  }

  getBackOfficeSettings(tenant: string, groupName: string): Call<SettingsGroupList> {
    const func = this.fetcher
      .path("/svc/settings/back-office/{tenant}/{group_name}")
      .method("get")
      .create();
    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func({ tenant, group_name: groupName }, init))
        .then(res => res.data as SettingsGroupList)
        .catch(ServiceError.raise),
    };
  }

  updateBackOfficeSettings(
    tenant: string,
    groupName: string,
    values: Record<string, unknown>
  ): Call<void> {
    const func = this.fetcher
      .path("/svc/settings/back-office/{tenant}/{group_name}")
      .method("post")
      .create();
    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init =>
          func({ tenant, group_name: groupName, values: values as SettingsValues }, init)
        )
        .then(() => undefined)
        .catch(ServiceError.raise),
    };
  }
}

let svcSettingsClientInstance: SvcSettingsClient | null = null;

export const getSvcSettingsClient = (): SvcSettingsClient => {
  if (!svcSettingsClientInstance) {
    svcSettingsClientInstance = new SvcSettingsClient(retrieveAuthToken);
  }
  return svcSettingsClientInstance;
};
